import React, { useEffect, useMemo, useRef, useState } from 'react'

import type { Product } from 'types'
// The tag-shop type SetDefaultTagModal actually declares. `types` exports a NARROWER
// `TagShopProduct` of the same name, so importing that one here would not satisfy the prop.
import type { TagShopProduct } from 'hooks/api/useTagProducts'

import { Stack, Typography } from '@mui/material'

import { Modal, Button, Alert } from '@repo/ui'
import { ProductSelectField, InheritedActivationTrigger, SetDefaultTagModal } from 'components'
import {
  useProducts,
  useAssignPackageProduct,
  PACKAGE_PRODUCT_CONFLICT_STATUS,
  type PackageBoundProduct,
  type PackageProductError,
} from 'hooks/api'

/**
 * VRG-282 — bind a whole package of tags to ONE of the brand's products.
 *
 * The point of the feature is to delete a Vera workflow from the contract manufacturer's floor:
 * if the box is bound before it ships, certificates already exist for every tag in it and the
 * job at the far end collapses to "put the tag on the object". So this modal is about the BOX,
 * never about individual tags — partial assignment would reintroduce "which tag is for what".
 */
interface AssignPackageProductModalProps {
  packageObj?: any
  open: boolean
  onClose: () => void
  refetch?: () => void
}

/**
 * The packages payload carries a THIN product projection (id/title/sku/image) — enough to
 * render, not enough to compare default tags. Widened to the shape `ProductPreview` reads so
 * the current binding can be shown before (or without) the products list resolving.
 */
const boundProductToPreview = (bound: PackageBoundProduct): Product =>
  ({
    id: bound.id,
    title: bound.title,
    sku: bound.sku ?? '',
    images: bound.image ? [bound.image] : [],
  }) as Product

export const AssignPackageProductModal = (props: AssignPackageProductModalProps) => {
  const { packageObj, open, onClose, refetch } = props

  const [selected, setSelected] = useState<Product | null>(null)
  /**
   * True while `selected` is the THIN payload projection rather than a real catalog record.
   * Tracked explicitly (instead of sniffing for a missing field) because the upgrade effect
   * below must not touch a selection the USER made — clobbering an in-progress pick when the
   * products list resolves is exactly the bug a single "seed on every products change" effect
   * would produce.
   */
  const [selectedIsStub, setSelectedIsStub] = useState(false)
  // Inline, not a toast: this is the answer to the Save the user just pressed, and the 409 case
  // ("something in here is already activated") is a REASON that has to stay on screen next to
  // the control it refuses. Toast policy — errors go inline.
  const [error, setError] = useState<string | null>(null)
  /**
   * The set-as-default follow-up, held as a SNAPSHOT of (product, tag) rather than read back off
   * `packageObj`.
   *
   * The binding modal closes the moment the write lands, and the Packages list clears its
   * `selectedPackage` on close — so by the time the prompt renders, `packageObj` is null and
   * anything derived from it (the tag being proposed) would be gone. Capturing both halves at
   * the moment the question is raised keeps the prompt answerable.
   */
  const [defaultTagPrompt, setDefaultTagPrompt] = useState<{
    product: Product
    tag: TagShopProduct
  } | null>(null)

  const assignProduct = useAssignPackageProduct()

  /**
   * The package this modal is SHOWING, which is not always the one it is currently handed.
   *
   * The shared `Modal` is `closeAfterTransition` over a `Fade`, so these children stay mounted
   * for the whole fade-out — while BOTH call sites drop their selected package the instant
   * `onClose` fires (`setAssignPackage(null)` / `setSelectedPackage(null)`). For those frames the
   * body re-rendered against nothing: the picker vanished and the "not assigned to a company yet"
   * warning flashed in on the way out, which is the flash you see around opening the next one.
   *
   * Holding the last package it was opened for makes the closing frames identical to the open
   * ones. It is not a delay — nothing is postponed or debounced; the modal simply keeps rendering
   * what it was already rendering until it is gone. WRITES still use the live `packageObj`.
   */
  const lastPackageRef = useRef<any>(null)
  if (packageObj) lastPackageRef.current = packageObj
  const shownPackage = packageObj ?? lastPackageRef.current

  // A package belongs to a company; its products are that company's. Internal staff open this
  // from the Packages list, so the company cannot be taken from the session.
  const companyId: string | null = shownPackage?.company_id ?? null
  const { data: productsData, refetch: refetchProducts } = useProducts(
    { limit: 200, company_id: companyId ?? undefined },
    { enabled: open && !!companyId },
  )
  const products = useMemo(() => productsData?.data ?? [], [productsData?.data])

  // The tag SKU this box was PURCHASED as — the thing a product's "default tag" is compared
  // against. Not the brand product; the two live on the same row under confusingly close names.
  const packageTag = shownPackage?.tag_shop_product ?? null

  const boundProduct: PackageBoundProduct | null = shownPackage?.product ?? null

  // Seed from the current binding when the modal OPENS, so it never opens looking unbound while
  // the catalog loads.
  useEffect(() => {
    if (!open) return
    setError(null)
    // The mutation lives as long as this component, which the Packages list keeps mounted across
    // every row. Without an explicit reset a 409 from ONE package would still be the mutation's
    // error state when the modal reopened for a DIFFERENT one, freezing a package that is
    // perfectly rebindable.
    assignProduct.reset()
    setSelected(boundProduct ? boundProductToPreview(boundProduct) : null)
    setSelectedIsStub(!!boundProduct)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, boundProduct?.id])

  // Upgrade that stub to the FULL catalog record once the list resolves — it carries
  // `tag_shop_product`, which the default-tag comparison needs and the payload projection has no
  // room for. Only ever replaces a stub, never a user's pick.
  useEffect(() => {
    if (!open || !selectedIsStub || !selected) return
    const full = products.find(entry => entry.id === selected.id)
    if (!full) return
    setSelected(full)
    setSelectedIsStub(false)
  }, [open, products, selectedIsStub, selected?.id])

  const handleClose = () => {
    setError(null)
    onClose()
  }

  const handleSave = () => {
    if (!packageObj) return
    setError(null)

    assignProduct.mutate(
      { id: String(packageObj.id), productId: selected?.id ?? null },
      {
        onSuccess: () => {
          refetch?.()
          // BIND FIRST, PROMPT AFTER — the set-as-default question is a follow-up, never a gate.
          // Because the write has already landed by the time this opens, declining the prompt
          // (or dismissing it) leaves the package bound, which is the required behaviour.
          //
          // `selected?.id`, not just `selected`: the prompt writes against the product's id, so
          // a selection that cannot name one has nothing to ask about and would only produce a
          // request that must fail.
          //
          // `default_tag_id`, NOT a nested `tag_shop_product`. A BRAND product records its
          // default tag as a flat id (`products.default_tag_id`, the tag SKU's Shopify product
          // id); only a PACKAGE carries a `tag_shop_product` object. Reading the nested shape
          // compared `undefined` against the tag id on every product, so the test was always
          // true and the prompt fired even for a product already defaulted to this exact tag —
          // the same defect `ProductSelectField` documents for its badge and ordering.
          //
          // And it asks only when there is NO default tag: that is what the prompt's copy says
          // ("does not have a default tag set yet"), and a product whose default is a different
          // tag has one deliberately. Overwriting that from a package-binding flow is a decision
          // the product page owns, not a follow-up question.
          const needsDefaultTagPrompt =
            !!selected?.id && !!packageTag?.id && !(selected as any)?.default_tag_id
          if (needsDefaultTagPrompt) {
            setDefaultTagPrompt({
              product: selected as Product,
              tag: packageTag as TagShopProduct,
            })
          }
          handleClose()
        },
        onError: (mutationError: PackageProductError) => {
          setError(mutationError?.message ?? 'Could not assign the product')
          // A 409 means the binding is frozen for good. Snap the field back to what is ACTUALLY
          // bound — leaving the refused pick on screen next to "can no longer be re-pointed"
          // would show a product the package is not bound to.
          if (mutationError?.status === PACKAGE_PRODUCT_CONFLICT_STATUS) {
            const full = boundProduct
              ? products.find(entry => entry.id === boundProduct.id)
              : undefined
            setSelected(
              full ?? (boundProduct ? boundProductToPreview(boundProduct) : null),
            )
            setSelectedIsStub(!!boundProduct && !full)
          }
        },
      },
    )
  }

  const isConflict =
    (assignProduct.error as PackageProductError | null)?.status ===
    PACKAGE_PRODUCT_CONFLICT_STATUS

  return (
    <>
      <Modal
        title="Assign product"
        open={open}
        onClose={handleClose}
        actions={
          <>
            <Button variant="secondary" onClick={handleClose} disabled={assignProduct.isPending}>
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleSave}
              // Nothing left to save once the server has frozen the binding — a live Save would
              // only earn the same 409 again.
              //
              // `!selected` — there is no such thing as saving "no product" here: this modal
              // exists to bind a box to one, and a live Save with an empty picker would send
              // `productId: null` and silently UNBIND a package the user only meant to re-point.
              disabled={assignProduct.isPending || !companyId || isConflict || !selected}
              loading={assignProduct.isPending}
            >
              Save
            </Button>
          </>
        }
      >
        <Stack spacing={6}>
          <Typography variant="body3" color="text.secondary" sx={{ lineHeight: 1.6 }}>
            {`Every tag in ${shownPackage?.name ?? 'this package'} becomes a certificate for the product you pick, so whoever receives the box only has to place the tag.`}
          </Typography>

          {!companyId && (
            // Products are scoped to a company, so an unassigned box has no catalog to pick from.
            // Said plainly here rather than showing an empty picker that looks broken.
            <Alert
              severity="warning"
              description="This package is not assigned to a company yet, so there are no products to bind it to. Assign it to a company first."
              sx={{ padding: '12px 24px' }}
            />
          )}

          {error && (
            <Alert
              severity="error"
              title={isConflict ? 'This package can no longer be re-pointed' : undefined}
              description={error}
              sx={{ padding: '12px 24px' }}
            />
          )}

          {!!companyId && (
            <ProductSelectField
              products={products}
              value={selected}
              onChange={product => {
                setError(null)
                setSelected(product)
              }}
              defaultForTagId={packageTag?.id ?? null}
              // Once a certificate in the box is live the binding is frozen server-side, so the
              // control is shown inert rather than inviting a change that will be refused again.
              readOnly={isConflict}
            />
          )}

          {/* The real tag count, so the panel can state the outcome ("creates and activates N
              certificates") rather than leaving the user to infer it. `quantity` is the box's
              actual tag count on both payloads this modal is opened from — the internal packages
              list and the brand's order page — not its purchased capacity. */}
          <InheritedActivationTrigger
            companyId={companyId}
            tagCount={shownPackage?.quantity}
          />
        </Stack>
      </Modal>

      {/* The set-as-default follow-up, in the SAME modal the product page uses for this decision
          (prompt mode) rather than a second one that writes the same endpoint. */}
      <SetDefaultTagModal
        product={defaultTagPrompt?.product ?? null}
        tagProduct={defaultTagPrompt?.tag ?? null}
        open={!!defaultTagPrompt}
        // Not a question mark. The modal already asks — it has Not now / Set as default under it
        // — so the title states what the modal IS, the way every other modal title here does.
        title="Set as default tag"
        // Names both halves, because the prompt is about a specific pairing and "this tag" makes
        // the reader look back at a modal that has already closed. The last line is load-bearing:
        // the binding has already been written, so declining costs nothing.
        description={`${defaultTagPrompt?.product?.title ?? 'This product'} does not have a default tag set yet. Set ${defaultTagPrompt?.tag?.title ?? 'this tag'} as the default tag for this product to more easily order tags for it and monitor inventory levels. This package stays bound either way.`}
        cancelLabel="Not now"
        confirmLabel="Set as default"
        onClose={() => setDefaultTagPrompt(null)}
        refetch={() => {
          refetchProducts()
          refetch?.()
        }}
      />
    </>
  )
}
